export type InvitationAuthUser = {
  uid: string;
  email: string | null;
};

type InvitationRedemptionOptions<User extends InvitationAuthUser> = {
  currentUser: User | null;
  normalizedEmail: string;
  createUser: () => Promise<User>;
  signInUser: () => Promise<User>;
  deleteUser: (user: User) => Promise<void>;
  redeemInvitation: (userId: string) => Promise<void>;
  onDeleteError?: (error: unknown) => void;
};

const errorCode = (error: unknown): string | undefined => {
  if (!error || typeof error !== 'object' || !('code' in error)) return undefined;
  return typeof error.code === 'string' ? error.code : undefined;
};

const AUTHORITATIVE_REJECTION_CODES = new Set([
  'functions/already-exists',
  'functions/failed-precondition',
  'functions/invalid-argument',
  'functions/not-found',
  'functions/permission-denied',
  'functions/resource-exhausted',
]);

export const redeemInvitationWithAuthAccount = async <User extends InvitationAuthUser>({
  currentUser,
  normalizedEmail,
  createUser,
  signInUser,
  deleteUser,
  redeemInvitation,
  onDeleteError,
}: InvitationRedemptionOptions<User>): Promise<User> => {
  const matchingCurrentUser =
    currentUser?.email?.trim().toLowerCase() === normalizedEmail.trim().toLowerCase();
  let user = matchingCurrentUser ? currentUser : null;
  let createdUser = false;

  if (!user) {
    try {
      user = await createUser();
      createdUser = true;
    } catch (error) {
      if (errorCode(error) !== 'auth/email-already-in-use') throw error;
      user = await signInUser();
    }
  }

  try {
    await redeemInvitation(user.uid);
    return user;
  } catch (error) {
    if (createdUser && AUTHORITATIVE_REJECTION_CODES.has(errorCode(error) ?? '')) {
      try {
        await deleteUser(user);
      } catch (deleteError) {
        onDeleteError?.(deleteError);
      }
    }
    throw error;
  }
};
